import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {EditSessionOfferingForm} from "../../../../components/offering-form/session-bundle/session";
import {
  editSessionBasicFormStepFormValidationSchema,
  EditSessionBasicFormStepFormValues,
} from "../schema-validation";
import {useGetSessionContext} from "../context/get-session";
import {ServiceFormLayout} from "../../createService/service-form-layout";
import {useEditService} from "../hooks/edit-service";
import {EditSessionFormValues} from "../types";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useDeleteSessionHook} from "../hooks/delete-service";
import {useEffect, useMemo} from "react";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {useOAuthCalendarResume} from "../../../../utils/google-calendar-resume";
import {useOfferingSchedules} from "@/features/availability";
import {scrollToFirstOfferingFormError} from "../../../../components/offering-form/accordion/scroll-to-first-error";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {toSavedEditSessionFormValues} from "../saved-form-values";

export function EditSessionBasicForm() {
  const navigate = useNavigate();
  const {
    state: {sessionData},
  } = useGetSessionContext();
  const {editSession, isEditingSession} = useEditService();
  const {deleteSession} = useDeleteSessionHook(sessionData.id);
  const snapshot = useOAuthCalendarResume<EditSessionBasicFormStepFormValues>();
  const {preferredSchedule} = useOfferingSchedules();

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "service",
    offeringId: sessionData.id,
    embedded: sessionData.cover_images,
  });

  // Convert domain format to API format for form state (memoized to prevent infinite loops)
  const coverImagesForForm = useMemo(
    () => mapDomainToEmbedded(coverImages),
    [coverImages]
  );

  const formMethods = useForm<EditSessionBasicFormStepFormValues>({
    resolver: yupResolver(editSessionBasicFormStepFormValidationSchema),
    mode: "onSubmit",
    defaultValues: snapshot ?? {
      ...toSavedEditSessionFormValues(
        sessionData,
        IS_PWYW_ENABLED,
        preferredSchedule?.id
      ),
      cover_images: sessionData.cover_images,
    },
  });
  // console.log({editformData: sessionData});

  // Sync cover images from v2 fetch to form state for validation
  useEffect(() => {
    formMethods.setValue("cover_images", coverImagesForForm);
    if (coverImagesForForm.length > 0) {
      formMethods.clearErrors("cover_images");
    } else {
      formMethods.trigger("cover_images");
    }
  }, [formMethods, coverImagesForForm]);

  function onSubmit(values: EditSessionBasicFormStepFormValues) {
    // console.log({values});
    const payload: EditSessionFormValues = {
      bookingQuestions: sessionData.bookingQuestions
        ? sessionData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: [
                  {
                    optionLabel: "",
                  },
                ],
              };
            }
          })
        : [],
      callDuration: values.callDuration,
      callLocation: values.callLocation,
      callPrice: values.callPrice,
      callPriceCurrency: values.callPriceCurrency,
      callPriceType: values.callPriceType,
      coverImage: values.coverImage,
      // Handed over as the form holds them; the DTO does the base-currency split.
      currencies: values.currencies,
      customCallDuration: values.customCallDuration,
      description: values.description,
      hideFromProfile: values.hideFromProfile,
      id: sessionData.id,
      isPaused: values.isPaused,
      isUrlRedirect: values.isUrlRedirect,
      name: values.name,
      requestCustomerPhoneNumber: values.requestCustomerPhoneNumber,
      slug: values.slug,
      url_redirect: values.url_redirect,
      redirectAfterPurchase: values.redirectAfterPurchase,
      telegramGroupId: values.telegramGroupId,
      cover_images: values.cover_images,
      availabilityScheduleId: values.availabilityScheduleId,
      enable_currency_conversion: true,
      selected_currencies: values.selected_currencies || [],
    };
    editSession(payload);
  }

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name ||
    formMethods.formState.errors.description ||
    formMethods.formState.errors.coverImage ||
    formMethods.formState.errors.slug ||
    formMethods.formState.errors.callDuration ||
    formMethods.formState.errors.customCallDuration;
  const isCoverImagesError = formMethods.formState.errors.cover_images;
  const isPricingError =
    formMethods.formState.errors.callPrice ||
    formMethods.formState.errors.callPriceCurrency ||
    formMethods.formState.errors.callPriceType ||
    formMethods.formState.errors.currencies;
  const isLocationError = formMethods.formState.errors.callLocation;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.redirectAfterPurchase ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.url_redirect;
  const isAvailabilityError = formMethods.formState.errors.availabilityScheduleId;
  // console.log({
  //   editwatch: formMethods.watch(),
  //   editerrors: formMethods.formState.errors,
  // });
  return (
    <ServiceFormLayout>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(
            onSubmit as any,
            scrollToFirstOfferingFormError
          )}
          autoComplete="off"
        >
          <Space direction="vertical" size={16} style={{display: "flex"}}>
            <EditSessionOfferingForm
              formType="edit"
              isGeneralInformationError={!!isGeneralInformationError}
              isLocationError={!!isLocationError}
              isPricingError={!!isPricingError}
              isSettingsError={!!isSettingsError}
              isCoverImagesError={!!isCoverImagesError}
              isAvailabilityError={!!isAvailabilityError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => {
                      navigate("/calls");
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType={"submit"}
                    size="small"
                    disabled={
                      Object.keys(formMethods.formState.errors).length > 0 ||
                      Object.values(formMethods.watch()).length < 1
                    }
                    loading={isEditingSession}
                  >
                    Update session
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this session? This action cannot be undone."
                    okText="Delete session"
                    onOk={deleteSession}
                    title="Confirm session deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
